/**
 * src/components/coach/CoachDashboardWrapper.tsx
 * 
 * Wrapper component that controls access to coach dashboard functionality.
 * Shows subscription status banner and limits functionality when subscription is not active.
 * Only allows full dashboard access when coach has active subscription status.
 * Updated to support internationalization with Spanish translations.
 */

import { Loader2, UserCheck } from 'lucide-react';
import React from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { useCoachSubscription } from '../../hooks/useCoachSubscription';
import { useI18n } from '../../lib/i18n/context';
import { SubscriptionStatusBanner } from './SubscriptionStatusBanner';

interface CoachDashboardWrapperProps {
  children: React.ReactNode;
  showLimitedAccess?: boolean;
}

export function CoachDashboardWrapper({ 
  children, 
  showLimitedAccess = true 
}: CoachDashboardWrapperProps) {
  const { profile } = useProfile();
  const { status, isLoading, error, canAccessDashboard } = useCoachSubscription();
  const { t } = useI18n();

  // Only apply restrictions to coach profiles
  if (!profile || profile.role !== 'coach') {
    return <>{children}</>;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-blue-600 dark:text-blue-400" />
          <p className="text-gray-600 dark:text-gray-400 text-sm">{t('loading')} account status...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-100 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-red-800 dark:text-red-200 text-sm">
              Unable to load account status: {error}
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 text-red-600 dark:text-red-400 hover:underline text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If coach has active subscription, show full dashboard
  if (canAccessDashboard) {
    return <>{children}</>;
  }

  // Limited access view for non-active subscriptions
  return (
    <div className="space-y-6">
      {/* Show subscription status banner */}
      <SubscriptionStatusBanner status={status} />
      
      {showLimitedAccess ? (
        <LimitedAccessView />
      ) : (
        // Show blocked access message
        <BlockedAccessView status={status} />
      )}
    </div>
  );
}

function LimitedAccessView() {
  const { t } = useI18n();

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 dark:bg-yellow-900/20">
          <UserCheck className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
        </div>
        <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('limited-access-mode')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {t('limited-access-description')}
        </p>
        
        <div className="mt-4 text-left max-w-md mx-auto">
          <ul className="text-sm text-gray-500 dark:text-gray-400 space-y-2">
            <li className="flex items-center">
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full mr-3"></div>
              {t('limited-access-features-1')}
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full mr-3"></div>
              {t('limited-access-features-2')}
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full mr-3"></div>
              {t('limited-access-features-3')}
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full mr-3"></div>
              {t('limited-access-features-4')}
            </li>
            <li className="flex items-center">
              <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full mr-3"></div>
              {t('limited-access-features-5')}
            </li>
          </ul>
        </div>
        
        <div className="mt-6">
          <p className="text-xs text-gray-400 dark:text-gray-500">
            {t('limited-access-review-time')}
          </p>
        </div>
      </div>
    </div>
  );
}

function BlockedAccessView({ status }: { status: string }) {
  const { t } = useI18n();

  const getBlockedMessage = (status: string) => {
    switch (status) {
      case 'suspended':
        return t('access-restricted-suspended');
      case 'cancelled':
        return t('access-restricted-cancelled');
      default:
        return t('access-restricted-default');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="text-center">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
          <UserCheck className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
        <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">
          {t('access-restricted')}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
          {getBlockedMessage(status)}
        </p>
      </div>
    </div>
  );
} 